import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Package, Receipt, Users, Tags, Ticket, Star, Gift,
  Calendar, Crown, Image as ImageIcon, Bell, ArrowLeftRight, BarChart3,
  Settings, Plug, ShieldCheck, Search, Plus, ChevronDown, Menu, LogOut,
  Sun, Moon, Key
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator
} from "../../components/ui/dropdown-menu";

export default function AdminLayout() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  if (loading) return <div className="min-h-screen bg-[#0F0F14] grid place-items-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const groups = [
    {
      label: null,
      items: [
        { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
      ],
    },
    {
      label: "GERENCIAMENTO",
      items: [
        { to: "/admin/products", icon: Package, label: "Produtos" },
        { to: "/admin/codes", icon: Key, label: "Códigos" },
        { to: "/admin/plans", icon: Crown, label: "Planos Premium" },
        { to: "/admin/orders", icon: Receipt, label: "Pedidos" },
        { to: "/admin/users", icon: Users, label: "Usuários" },
        { to: "/admin/categories", icon: Tags, label: "Categorias" },
        { to: "/admin/coupons", icon: Ticket, label: "Cupons" },
        { to: "/admin/reviews", icon: Star, label: "Reviews" },
        { to: "/admin/gift-cards", icon: Gift, label: "Gift Cards" },
        { to: "/admin/subscriptions", icon: Calendar, label: "Assinaturas" },
        { to: "/admin/premium-accounts", icon: Crown, label: "Contas Premium" },
      ],
    },
    {
      label: "CONTEÚDO",
      items: [
        { to: "/admin/banners", icon: ImageIcon, label: "Banners" },
        { to: "/admin/notifications", icon: Bell, label: "Notificações" },
      ],
    },
    {
      label: "FINANCEIRO",
      items: [
        { to: "/admin/transactions", icon: ArrowLeftRight, label: "Transações" },
        { to: "/admin/reports", icon: BarChart3, label: "Relatórios" },
      ],
    },
    {
      label: "CONFIGURAÇÕES",
      items: [
        { to: "/admin/settings", icon: Settings, label: "Configurações" },
        { to: "/admin/integrations", icon: Plug, label: "Integrações" },
        { to: "/admin/permissions", icon: ShieldCheck, label: "Permissões" },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#0F0F14] text-slate-200" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full bg-[#171723] border-r border-white/5 transition-all duration-300 z-40 ${sidebarOpen ? "w-[260px]" : "w-0 overflow-hidden"}`}>
        <div className="p-6 border-b border-white/5">
          <Link to="/admin" className="flex items-center gap-2">
            <img src="/brand/gameflow-logo-64.png" alt="GAMEFLOW" className="w-9 h-9 rounded-xl shadow-neon-soft" />
            <span className="font-display text-lg font-bold text-white">GAME<span className="text-[#A855F7]">FLOW</span></span>
          </Link>
        </div>

        <nav className="p-4 overflow-y-auto h-[calc(100vh-200px)]">
          {groups.map((g, gi) => (
            <div key={gi} className={gi > 0 ? "mt-5" : ""}>
              {g.label && <div className="text-[10px] font-semibold text-slate-500 tracking-wider px-3 mb-2">{g.label}</div>}
              <div className="space-y-0.5">
                {g.items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        isActive
                          ? "bg-gradient-to-r from-[#A855F7] to-[#7E22CE] text-white shadow-neon-soft"
                          : "text-slate-400 hover:text-white hover:bg-white/5"
                      }`
                    }
                    data-testid={`admin-nav-${it.label}`}
                  >
                    <it.icon className="w-4 h-4" />
                    {it.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="absolute bottom-4 left-4 right-4">
          <div className="relative rounded-2xl border border-[#A855F7]/30 p-4 text-center overflow-hidden">
            <img src="/brand/gameflow-banner.jpg" alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#A855F7]/40 to-[#7E22CE]/60" />
            <div className="relative">
              <Crown className="w-5 h-5 text-[#FBBF24] mx-auto mb-2 drop-shadow" />
              <div className="font-display font-bold text-sm text-white drop-shadow">GAMEFLOW PREMIUM</div>
              <p className="text-xs text-slate-100/90 mt-1">Advanced reports & more</p>
              <button onClick={() => navigate("/admin/plans")} className="mt-3 w-full h-9 rounded-lg bg-white text-[#7E22CE] text-xs font-bold hover:bg-slate-100">Manage Plans</button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className={`transition-all duration-300 ${sidebarOpen ? "ml-[260px]" : "ml-0"}`}>
        {/* Topbar */}
        <header className="sticky top-0 z-30 bg-[#0F0F14]/80 backdrop-blur-xl border-b border-white/5">
          <div className="px-6 lg:px-8 h-[70px] flex items-center gap-4">
            <button onClick={() => setSidebarOpen((v) => !v)} className="p-2 rounded-lg text-slate-400 hover:bg-white/5 hover:text-white" data-testid="admin-sidebar-toggle">
              <Menu className="w-5 h-5" />
            </button>

            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                placeholder="Buscar jogos, pedidos, usuários..."
                className="w-full pl-10 pr-4 h-10 rounded-full bg-white/5 border border-white/10 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#A855F7]/50"
                data-testid="admin-search"
              />
            </div>

            <div className="ml-auto flex items-center gap-2">
              <button className="relative p-2 rounded-lg text-slate-400 hover:bg-white/5 hover:text-white">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#A855F7] rounded-full" />
              </button>
              <button className="p-2 rounded-lg text-slate-400 hover:bg-white/5 hover:text-white">
                <Moon className="w-5 h-5" />
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 transition-colors" data-testid="admin-user-menu">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#A855F7] to-[#7E22CE] grid place-items-center text-white text-sm font-semibold">
                      {user.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="hidden sm:block text-left">
                      <div className="text-sm font-semibold text-white">{user.name}</div>
                      <div className="text-[10px] text-slate-400">Super Administrador</div>
                    </div>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/")}>Ver site</DropdownMenuItem>
                  <DropdownMenuItem onClick={logout}><LogOut className="w-4 h-4 mr-2" /> Sair</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <button onClick={() => navigate("/admin/products")} className="hidden sm:flex items-center gap-2 h-10 px-4 rounded-lg bg-[#A855F7] text-white font-semibold text-sm hover:bg-[#9333EA] transition-colors" data-testid="admin-add-product">
                <Plus className="w-4 h-4" /> Adicionar Produto
              </button>
            </div>
          </div>
        </header>

        <main className="p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
